import { DibblyMark } from "@/components/tuw/DibblyLogo"

// The dark right-hand panel of the split auth layout. Copy changes with the
// screen family (design/spec/auth/auth.html `panel()`); the sign up variant
// carries the real four-step how-it-works from the legacy registration page.
const COPY = {
  signin: {
    lead: "Your projects, your team, ",
    emphasis: "one place.",
    body: "Review drafts, approve designs, message your freelancers and start your next project from the dashboard.",
  },
  signup: {
    lead: "You’re a few steps from ",
    emphasis: "content you’ll be proud of.",
    body: "Post what you need, choose your package, and pick from vetted writers, editors, designers and narrators.",
    steps: [
      "Create your account",
      "Post a project and choose your package",
      "Browse and select a freelancer",
      "Follow your content as it’s created",
    ],
  },
  reset: {
    lead: "Locked out? ",
    emphasis: "It happens.",
    body: "Your projects, files and conversations are exactly where you left them. We’ll get you back in.",
  },
}

export default function BrandPanel({ variant = "signin" }) {
  const copy = COPY[variant] ?? COPY.signin

  return (
    <aside
      aria-hidden="true"
      className="relative flex min-w-0 flex-col justify-between overflow-hidden bg-[image:var(--railbar-bg)] px-[52px] py-11 text-railbar-foreground max-[980px]:hidden dark:border-l dark:border-border"
    >
      <div className="text-[12px] font-bold uppercase tracking-[0.1em] text-railbar-foreground/50">
        The Urban Writers · by Dibbly
      </div>

      <div className="relative z-[1] max-w-[460px]">
        <h2 className="font-display text-[36px] font-bold leading-[1.12] tracking-[-1px] [text-wrap:pretty]">
          {copy.lead}
          <em className="not-italic text-primary">{copy.emphasis}</em>
        </h2>
        <p className="mt-4 max-w-[42ch] text-[15px] leading-[1.6] text-railbar-foreground/[0.68] [text-wrap:pretty]">
          {copy.body}
        </p>
        {copy.steps && (
          <ol className="mt-8 flex flex-col border-t border-railbar-foreground/[0.12]">
            {copy.steps.map((step, index) => (
              <li
                key={step}
                className={
                  index === 0
                    ? "flex items-center gap-4 border-b border-railbar-foreground/[0.12] py-3.5 text-[14.5px] font-medium text-railbar-foreground"
                    : "flex items-center gap-4 border-b border-railbar-foreground/[0.12] py-3.5 text-[14.5px] font-medium text-railbar-foreground/[0.72]"
                }
              >
                <i
                  className={
                    index === 0
                      ? "flex size-[26px] shrink-0 items-center justify-center rounded-full border-[1.5px] border-primary bg-primary font-display text-[12px] font-bold not-italic text-primary-foreground"
                      : "flex size-[26px] shrink-0 items-center justify-center rounded-full border-[1.5px] border-railbar-foreground/30 font-display text-[12px] font-bold not-italic"
                  }
                >
                  {index + 1}
                </i>
                {step}
              </li>
            ))}
          </ol>
        )}
      </div>

      <div className="relative z-[1] flex flex-col gap-1.5 text-[13px] text-railbar-foreground/55">
        <b className="font-semibold text-railbar-foreground/85">Posting a project is free.</b>
        <span>You only pay once a freelancer accepts your project.</span>
      </div>

      <div className="pointer-events-none absolute -right-[90px] -bottom-[110px] size-[440px] opacity-[0.08]">
        <DibblyMark className="size-full" />
      </div>
    </aside>
  )
}
